import { z } from 'zod';
// Contracts of the read-only API (Phase 6.5). All objects are strict: a response with any field
// beyond these fails to parse on the server, so nothing extra (purchasers, contacts, documents,
// credentials, financial data) can leak by accident.
const permissionKeys = z.array(z.string());
/** `GET /me`: minimal profile and capabilities, for UI gating only. The server decides every request. */
export const meResponseSchema = z
    .object({
    user: z.object({ id: z.uuid(), displayName: z.string() }).strict(),
    systemOwner: z.boolean(),
    /** How this session was identified: `oidc` or `supabase` sign-in, or `dev-local`, the synthetic development adapter (not authentication). */
    identityMode: z.enum(['dev-local', 'oidc', 'supabase']),
    access: z
        .object({
        organization: permissionKeys,
        allProjects: permissionKeys,
        projects: z.array(z.object({ projectId: z.uuid(), permissions: permissionKeys }).strict()),
    })
        .strict(),
})
    .strict();
export const pageSchema = z
    .object({
    limit: z.number().int().min(1),
    offset: z.number().int().min(0),
    /** Number of items visible to the caller, counted after permission filtering. */
    total: z.number().int().min(0),
})
    .strict();
export const projectSummarySchema = z
    .object({
    id: z.uuid(),
    code: z.string(),
    name: z.string(),
    city: z.string(),
    status: z.enum(['setup', 'active', 'warranty', 'closed']),
})
    .strict();
/** `GET /projects`. */
export const projectListResponseSchema = z
    .object({ items: z.array(projectSummarySchema), page: pageSchema })
    .strict();
export const UNIT_KINDS = ['apartment', 'parking', 'storage', 'commercial'];
export const UNIT_STATUSES = ['planned', 'active', 'retired'];
/** One unit as listed and edited in the master-data grid. `rowVersion` is required for updates. */
export const unitSummarySchema = z
    .object({
    id: z.uuid(),
    code: z.string(),
    displayNumber: z.string(),
    kind: z.enum(UNIT_KINDS),
    structureId: z.uuid(),
    levelId: z.uuid().nullable(),
    modelType: z.string().nullable(),
    rooms: z.number().nullable(),
    areaM2: z.number().nullable(),
    status: z.enum(UNIT_STATUSES),
    rowVersion: z.number().int().min(1),
})
    .strict();
/** `GET /projects/:projectId/units`. */
export const unitListResponseSchema = z
    .object({ items: z.array(unitSummarySchema), page: pageSchema })
    .strict();
/** `GET /auth/session` (AUTH_MODE=oidc): the signed-in session's CSRF token for writes. */
export const authSessionResponseSchema = z
    .object({ userId: z.uuid(), csrfToken: z.string().regex(/^[A-Za-z0-9_-]{43}$/) })
    .strict();
/** `GET /auth/methods` (AUTH_MODE=supabase): sign-in happens in the application's own form. */
export const authMethodsResponseSchema = z.object({ method: z.literal('password') }).strict();
/**
 * The next step of a sign-in (`POST /auth/password`, `/auth/password/set`, `/auth/mfa`):
 * - `signed-in`: the session cookie is set;
 * - `mfa`: enter the code of the authenticator app;
 * - `enroll`: first set up an authenticator app (QR code image as a data URL, or the key typed in).
 * A refused sign-in answers 401 with a reason code (SIGN_IN_ERRORS), never with details.
 */
export const signInStepSchema = z.discriminatedUnion('step', [
    z.object({ step: z.literal('signed-in') }).strict(),
    z.object({ step: z.literal('mfa') }).strict(),
    z
        .object({
        step: z.literal('enroll'),
        qrCode: z.string().regex(/^data:image\/svg\+xml[;,]/),
        secret: z.string().regex(/^[A-Z2-7]{16,128}$/),
        /** The same key as an `otpauth://` address: opens the authenticator app on a phone. */
        uri: z
            .string()
            .max(2000)
            .regex(/^otpauth:\/\/totp\//)
            .optional(),
    })
        .strict(),
]);
export const SIGN_IN_LIMITS = { email: 254, password: { min: 10, max: 128 }, code: 6 };
export const signInRequestSchema = z
    .object({
    email: z.string().trim().toLowerCase().max(SIGN_IN_LIMITS.email).pipe(z.email()),
    password: z.string().min(1).max(SIGN_IN_LIMITS.password.max),
})
    .strict();
export const setPasswordRequestSchema = z
    .object({
    kind: z.enum(['invite', 'recovery']),
    token: z.string().regex(/^[A-Za-z0-9_-]{20,200}$/),
    password: z.string().min(SIGN_IN_LIMITS.password.min).max(SIGN_IN_LIMITS.password.max),
    /** An invitation: the work address it was made for, typed by its recipient. */
    email: z.string().trim().toLowerCase().max(SIGN_IN_LIMITS.email).pipe(z.email()).optional(),
})
    .strict();
export const mfaRequestSchema = z.object({ code: z.string().regex(/^\d{6}$/) }).strict();
export const recoverRequestSchema = z
    .object({ email: z.string().trim().toLowerCase().max(SIGN_IN_LIMITS.email).pipe(z.email()) })
    .strict();
/** Why a sign-in step was refused (the `code` of the 401/429 answer), in Hebrew. */
export const SIGN_IN_ERRORS = {
    invalid_credentials: 'כתובת הדוא״ל או הסיסמה שגויות.',
    invalid_code: 'הקוד שגוי או שפג תוקפו. הקלידו את הקוד המוצג כעת באפליקציה.',
    expired: 'תם הזמן להשלמת הכניסה. התחילו שוב.',
    not_registered: 'אין לחשבון זה גישה למערכת. לבירור פנו למנהלי המערכת.',
    inactive: 'החשבון אינו פעיל. לבירור פנו למנהלי המערכת.',
    invalid_link: 'הקישור אינו תקף, פג תוקפו או שכבר נעשה בו שימוש. בקשו קישור חדש.',
    wrong_email: 'כתובת הדוא״ל אינה הכתובת שההזמנה נוצרה עבורה. הקלידו את כתובת העבודה שלכם.',
    weak_password: 'הסיסמה חלשה מדי. בחרו סיסמה ארוכה יותר, שאינה נפוצה.',
    too_many: 'יותר מדי ניסיונות. המתינו כמה דקות ונסו שוב.',
    unavailable: 'שירות הכניסה אינו זמין כרגע. נסו שוב בעוד מספר דקות.',
};
/** `POST /auth/logout`: where the browser goes after the session ended. */
export const logoutResponseSchema = z.object({ redirectUrl: z.string() }).strict();
//# sourceMappingURL=read-api.js.map